import { cn } from '@/utils';
import { Icons } from '@/utils/icons';
import React, { useState } from 'react';
import { useController, Control, FieldValues, Path } from 'react-hook-form';

interface CustomInputProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  helperText?: string;
  passwordToggleClassName?: string;

  // NEW
  type?: 'text' | 'password' | 'email' | 'number';

  inputType?: 'text' | 'alphanumeric' | 'numeric' | 'decimal' | 'alphabets';
  maxLength?: number;
}

const CustomInput = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  required = false,
  disabled = false,
  className = '',
  inputClassName = '',
  labelClassName = '',
  helperText,
  passwordToggleClassName = '',
  type = 'text',
  inputType = 'text',
  maxLength,
}: CustomInputProps<T>) => {
  const [showPassword, setShowPassword] = useState(false);

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      field.onChange('');
      return;
    }

    let isValid = true;
    let regex: RegExp;

    switch (inputType) {
      case 'numeric':
        regex = /^\d+$/;
        isValid = regex.test(value);
        break;
      case 'decimal':
        regex = /^\d*\.?\d*$/;
        isValid = regex.test(value);
        break;
      case 'alphabets':
        regex = /^[a-zA-Z\s]*$/;
        isValid = regex.test(value);
        break;
      case 'alphanumeric':
        regex = /^[a-zA-Z0-9\s]*$/;
        isValid = regex.test(value);
        break;
      default:
        isValid = true;
    }

    if (isValid) {
      field.onChange(value);
    }
  };

  const resolvedType = type === 'password' ? (showPassword ? 'text' : 'password') : type;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className={`block text-sm font-medium text-black mb-2 ${labelClassName}`}
        >
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          {...field}
          id={name}
          type={resolvedType}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          onChange={handleInputChange}
          className={`
            w-full transition-all duration-200 placeholder:text-13 placeholder:text-black/80
            outline-none disabled:opacity-50 disabled:cursor-not-allowed border-black ${inputClassName}
          `}
        />

        {/* Password Toggle */}
        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className={cn(
              'absolute right-1 top-1/2 -translate-y-1/2 text-black',
              passwordToggleClassName
            )}
          >
            {showPassword ? <Icons.openEye size={16} /> : <Icons.closeEye size={18} />}
          </button>
        )}
      </div>

      {helperText && !error && <p className="mt-1 text-xs text-gray-500">{helperText}</p>}

      <p className="h-3 text-10 md:text-xs text-red-500">{error ? error.message : ''}</p>
    </div>
  );
};

export default CustomInput;
